import { useState, useEffect } from 'react';

/**
 * Animates a number from 0 to target value
 * @param {number} target - Target number to animate to
 * @param {number} duration - Animation duration in ms (default 2000)
 * @param {boolean} shouldStart - Whether to start the animation (default true)
 * @returns {number} Current animated value
 */
export function useAnimatedNumber(target, duration = 2000, shouldStart = true) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!shouldStart || target === 0) {
      setValue(target);
      return;
    }

    const startTime = performance.now();
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out cubic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(eased * target);
      
      setValue(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setValue(target);
      }
    };

    requestAnimationFrame(animate);
  }, [target, duration, shouldStart]);

  return value;
}

export default useAnimatedNumber;
